import loc, { type LocalizedString, type ResourceId } from 'owa-localize';
import { executePrompt } from 'owa-copilot-dab/lib/utils/executePrompt';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import { getCMMIDForInboxBriefing } from 'owa-mail-inbox-briefings-utils/lib/utils/getCMMIDForInboxBriefing';
import { getInboxBriefingCommandText } from 'owa-mail-inbox-briefings-utils/lib/utils/getInboxBriefingCommandText';
import { isMorningBriefing } from 'owa-mail-inbox-briefings-utils/lib/utils/currentTimeCheckUtils';
import { logInboxBriefingsTelemetryEvent } from 'owa-copilot-telemetry/lib/inboxBriefingsTelemetryLogger';
import { LazyGenerateProactiveDrafts } from 'owa-mail-proactive-drafts-store';
import {
    COMPOSE_FORWARD_EXPLAIN_CMM_ID,
    COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
    COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
    COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
    COMPOSE_NEW_HELP_WRITE_CMM_ID,
    COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
    COMPOSE_REPLY_HELP_REPLY_CMM_ID,
    COMPOSE_REPLY_KEY_POINTS_CMM_ID,
    COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
    FIND_QUICK_WINS_CMM_ID,
    GET_ORIENTED_CMM_ID,
    SUGGEST_NEXT_STEPS_CMM_ID,
    SUMMARIZE_AND_REPLY_CMM_ID,
    SUMMARIZE_CMM_ID,
    TRIAGE_MY_INBOX_CMM_ID,
} from 'owa-bizchat-shared/lib/utils/ScenarioCMMIds';
import {
    composeNewHelpWriteCommandText,
    composeNewHelpWriteDisplayText,
    composeNewFindRecipientsCommandText,
    composeNewFindRecipientsDisplayText,
    composeNewSupportStatementCommandText,
    composeNewSupportStatementDisplayText,
    composeReplyHelpReplyCommandText,
    composeReplyHelpReplyDisplayText,
    composeReplyNextStepsCommandText,
    composeReplyNextStepsDisplayText,
    composeReplyKeyPointsCommandText,
    composeReplyKeyPointsDisplayText,
    composeForwardExplainCommandText,
    composeForwardExplainDisplayText,
    composeForwardWithContextCommandText,
    composeForwardWithContextDisplayText,
    composeForwardWithTasksCommandText,
    composeForwardWithTasksDisplayText,
    findQuickWinsCommandText,
    findQuickWinsDisplayText,
    getOrientedCommandText,
    getOrientedDisplayText,
    suggestNextStepsCommandText,
    suggestNextStepsDisplayText,
    summarizeAndReplyCommandText,
    summarizeAndReplyDisplayText,
    summarizeCommandText,
    summarizeDisplayText,
    triageMyInboxCommandText,
    triageMyInboxDisplayText,
    inboxMorningBriefingDisplayText,
    inboxEveningBriefingDisplayText,
    generateProactiveDraftsDisplayText,
} from './suggestionCatalog.locstring.json';
import type { MailboxInfo } from 'owa-client-types';
import { isDraftAssistantEnabled } from 'owa-copilot-settings-utils/lib/utils/isDraftAssistantEnabled';

type BaseSuggestion = {
    /** Unique identifier */
    id: string;
    /** Callback function to execute when the suggestion is clicked */
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) => void;
};

function executePromptWithAgent(
    promptText: LocalizedString,
    formCode: string,
    targetWindow: Window,
    mailboxInfo: MailboxInfo
) {
    executePromptWithQueryAnnotation(
        promptText,
        formCode,
        targetWindow,
        mailboxInfo,
        undefined,
        isDraftAssistantEnabled(mailboxInfo) ? 'DraftAssistant' : undefined
    );
}

// Compose DAB SUAs - New Email
export const composeNewHelpWrite: Suggestion = {
    displayText: composeNewHelpWriteDisplayText,
    id: 'composeNewHelpWrite',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewHelpWriteCommandText),
            COMPOSE_NEW_HELP_WRITE_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeNewFindRecipients: Suggestion = {
    displayText: composeNewFindRecipientsDisplayText,
    id: 'composeNewFindRecipients',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewFindRecipientsCommandText),
            COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeNewSupportStatement: Suggestion = {
    displayText: composeNewSupportStatementDisplayText,
    id: 'composeNewSupportStatement',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewSupportStatementCommandText),
            COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Compose DAB SUAs - Reply
export const composeReplyHelpReply: Suggestion = {
    displayText: composeReplyHelpReplyDisplayText,
    id: 'composeReplyHelpReply',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyHelpReplyCommandText),
            COMPOSE_REPLY_HELP_REPLY_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeReplyNextSteps: Suggestion = {
    displayText: composeReplyNextStepsDisplayText,
    id: 'composeReplyNextSteps',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyNextStepsCommandText),
            COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeReplyKeyPoints: Suggestion = {
    displayText: composeReplyKeyPointsDisplayText,
    id: 'composeReplyKeyPoints',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyKeyPointsCommandText),
            COMPOSE_REPLY_KEY_POINTS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Compose DAB SUAs - Forward
export const composeForwardExplain: Suggestion = {
    displayText: composeForwardExplainDisplayText,
    id: 'composeForwardExplain',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardExplainCommandText),
            COMPOSE_FORWARD_EXPLAIN_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeForwardWithContext: Suggestion = {
    displayText: composeForwardWithContextDisplayText,
    id: 'composeForwardWithContext',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardWithContextCommandText),
            COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeForwardWithTasks: Suggestion = {
    displayText: composeForwardWithTasksDisplayText,
    id: 'composeForwardWithTasks',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardWithTasksCommandText),
            COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Ensures that one of either `displayText` or `displayTextLocalized` is defined
// TODO: Eventually, using only `displayText: LocalizedString` would be ideal
export type Suggestion =
    | (BaseSuggestion & {
          displayText: ResourceId;
          displayTextLocalized?: never;
      })
    | (BaseSuggestion & {
          displayText?: never;
          displayTextLocalized: LocalizedString | string;
      });

type NonEmptyArray<T> = [T, ...T[]];

export type Suggestions = NonEmptyArray<Suggestion> | undefined;

export const findQuickWins: Suggestion = {
    displayText: findQuickWinsDisplayText,
    id: 'findQuickWins',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePrompt(
            loc(findQuickWinsCommandText),
            FIND_QUICK_WINS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const getOriented: Suggestion = {
    displayText: getOrientedDisplayText,
    id: 'getOriented',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePrompt(loc(getOrientedCommandText), GET_ORIENTED_CMM_ID, targetWindow, mailboxInfo),
};

export const suggestNextSteps: Suggestion = {
    displayText: suggestNextStepsDisplayText,
    id: 'suggestNextSteps',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(suggestNextStepsCommandText),
            SUGGEST_NEXT_STEPS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const summarize: Suggestion = {
    displayText: summarizeDisplayText,
    id: 'summarize',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(summarizeCommandText),
            SUMMARIZE_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const summarizeAndReply: Suggestion = {
    displayText: summarizeAndReplyDisplayText,
    id: 'summarizeAndReply',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(summarizeAndReplyCommandText),
            SUMMARIZE_AND_REPLY_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const triageMyInbox: Suggestion = {
    displayText: triageMyInboxDisplayText,
    id: 'triageMyInbox',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePrompt(
            loc(triageMyInboxCommandText),
            TRIAGE_MY_INBOX_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const inboxMorningBriefing: Suggestion = {
    displayText: inboxMorningBriefingDisplayText,
    id: 'inboxMorningBriefing',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        onInboxBriefingSuggestionClick(targetWindow, mailboxInfo),
};

export const inboxEveningBriefing: Suggestion = {
    displayText: inboxEveningBriefingDisplayText,
    id: 'inboxEveningBriefing',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        onInboxBriefingSuggestionClick(targetWindow, mailboxInfo),
};

export const generateProactiveDrafts: Suggestion = {
    displayText: generateProactiveDraftsDisplayText,
    id: 'generateProactiveDrafts',
    onClick: (_targetWindow: Window, mailboxInfo: MailboxInfo) => {
        // Fire-and-forget: async operation runs without blocking the caller
        void LazyGenerateProactiveDrafts.importAndExecute(mailboxInfo);
    },
};

function onInboxBriefingSuggestionClick(targetWindow: Window, mailboxInfo: MailboxInfo) {
    logInboxBriefingsTelemetryEvent('InboxBriefingsTriggered', mailboxInfo, {
        source: 'SUA',
        briefingType: isMorningBriefing() ? 'morning' : 'evening',
    });
    executePrompt(
        getInboxBriefingCommandText(mailboxInfo),
        getCMMIDForInboxBriefing(),
        targetWindow,
        mailboxInfo
    );
}
